/**
 * Утилиты для работы с localStorage
 */

import { STORAGE_KEYS, LIMITS } from '@/constants/defaults';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Проверяет доступность localStorage
 * @returns true если localStorage доступен
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Сохраняет значение в localStorage
 * @param key - ключ
 * @param value - значение (будет сериализовано в JSON)
 * @returns true если успешно
 */
export function setStorageItem<T>(key: string, value: T): boolean {
  if (!isLocalStorageAvailable()) {
    chatLogger.warn('[Storage] localStorage недоступен');
    return false;
  }

  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    
    // Обновляем временную метку последнего изменения
    localStorage.setItem(STORAGE_KEYS.LAST_UPDATED, Date.now().toString());
    
    return true;
  } catch (error) {
    chatLogger.error('[Storage] Ошибка сохранения:', error);
    return false;
  }
}

/**
 * Получает значение из localStorage
 * @param key - ключ
 * @param defaultValue - значение по умолчанию
 * @returns значение или defaultValue
 */
export function getStorageItem<T>(key: string, defaultValue: T): T {
  if (!isLocalStorageAvailable()) {
    return defaultValue;
  }

  try {
    const item = localStorage.getItem(key);
    
    if (item === null) {
      return defaultValue;
    }

    return JSON.parse(item) as T;
  } catch (error) {
    chatLogger.error('[Storage] Ошибка чтения:', error);
    return defaultValue;
  }
}

/**
 * Удаляет значение из localStorage
 * @param key - ключ
 * @returns true если успешно
 */
export function removeStorageItem(key: string): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    chatLogger.error('[Storage] Ошибка удаления:', error);
    return false;
  }
}

/**
 * Очищает все данные виджета из localStorage
 * @returns true если успешно
 */
export function clearAllStorage(): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    return true;
  } catch (error) {
    chatLogger.error('[Storage] Ошибка очистки:', error);
    return false;
  }
}

/**
 * Проверяет устаревшие данные и очищает их
 * @returns количество удаленных ключей
 */
export function cleanupExpiredData(): number {
  if (!isLocalStorageAvailable()) {
    return 0;
  }

  const lastUpdated = getStorageItem<number>(STORAGE_KEYS.LAST_UPDATED, 0);
  const now = Date.now();
  const ttlMs = LIMITS.STORAGE_TTL_DAYS * 24 * 60 * 60 * 1000;

  // Если данные старше TTL, очищаем
  if (lastUpdated && now - lastUpdated > ttlMs) {
    clearAllStorage();
    chatLogger.log('[Storage] Очищены устаревшие данные');
    return Object.keys(STORAGE_KEYS).length;
  }

  return 0;
}

/**
 * Получает размер данных в localStorage (приблизительно)
 * @returns размер в байтах
 */
export function getStorageSize(): number {
  if (!isLocalStorageAvailable()) {
    return 0;
  }

  let total = 0;

  Object.values(STORAGE_KEYS).forEach((key) => {
    const item = localStorage.getItem(key);
    if (item) {
      total += item.length + key.length;
    }
  });

  return total;
}

